import React, { useState, useEffect } from "react";
import "./styles.css";

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    age: "",
    gender: "",
    nationality: "",
    email: "",
    opinion: "",
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    // Loading
    setTimeout(() => setIsLoading(false), 2000);

    // Scroll effects
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Parallax for images
      const parallaxElements =
        document.querySelectorAll<HTMLElement>(".parallax");
      parallaxElements.forEach((element) => {
        const speed = element.dataset.speed || "0.5";
        const yPos = -(window.scrollY * parseFloat(speed));
        element.style.transform = `translateY(${yPos}px)`;
      });
    };

    window.addEventListener("scroll", handleScroll);

    // Intersection Observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    document
      .querySelectorAll(
        ".fade-in-up, .about-text, .about-visual, .works-intro, .work-card, .contact-content, .form-container",
      )
      .forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = "Please enter your name";
    } else if (formData.name.length > 100) {
      errors.name = "Name must be less than 100 characters";
    } else if (/<script/i.test(formData.name)) {
      errors.name = "Invalid characters in name";
    }

    // Age validation
    if (!formData.age) {
      errors.age = "Please enter your age";
    } else if (
      isNaN(Number(formData.age)) ||
      Number(formData.age) < 1 ||
      Number(formData.age) > 150
    ) {
      errors.age = "Please enter a valid age";
    }

    // Gender validation
    if (!formData.gender) {
      errors.gender = "Please select your gender";
    }

    // Nationality validation
    if (!formData.nationality.trim()) {
      errors.nationality = "Please enter your nationality";
    } else if (formData.nationality.length > 100) {
      errors.nationality = "Nationality must be less than 100 characters";
    } else if (/<script/i.test(formData.nationality)) {
      errors.nationality = "Invalid characters in nationality";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      errors.email = "Please enter your email";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email";
    }

    // Message validation
    if (!formData.opinion.trim()) {
      errors.opinion = "Please enter your message";
    } else if (formData.opinion.length > 1000) {
      errors.opinion = "Message must be within 1000 characters";
    } else if (/<script/i.test(formData.opinion)) {
      errors.opinion = "Invalid characters in message";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Lambda URL from environment variable
        const LAMBDA_URL = import.meta.env.VITE_LAMBDA_URL;

        const response = await fetch(LAMBDA_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            referrer: document.referrer || "direct",
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          alert("Thank you for your inquiry. We will contact you soon.");
          // Reset form
          setFormData({
            name: "",
            age: "",
            gender: "",
            nationality: "",
            email: "",
            opinion: "",
          });
        } else {
          throw new Error(result.message || "Failed to submit form");
        }
      } catch (error) {
        console.error("Form submission error:", error);
        alert(
          "Sorry, there was an error submitting your form. Please try again later.",
        );
      }
    }
  };

  return (
    <div className="App">
      {/* Loading Screen */}
      <div className={`loading-screen ${!isLoading ? "fade-out" : ""}`}>
        <div className="loading-logo-container">
          <img src="/logo.png" alt="HOKUSAI" className="loading-logo-img" />
        </div>
      </div>

      {/* Header */}
      <header className={`header-main ${isScrolled ? "scrolled" : ""}`}>
        <div className="header-container">
          <a href="#" className="logo-link">
            <img src="/logo.png" alt="HOKUSAI" className="logo-img" />
            {/* <span className="logo-text">HOKUSAI WORKS WORLD TOUR</span> */}
          </a>
          <nav className="nav-main">
            <a href="#contact" className="nav-link">
              NEWSLETTER
            </a>
            <a href="#works" className="nav-link">
              WORKS
            </a>
            <a href="#about" className="nav-link">
              ABOUT
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            <span className="menu-icon">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav className={`mobile-nav ${isMobileMenuOpen ? "active" : ""}`}>
        <a
          href="#contact"
          className="mobile-nav-link"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          NEWSLETTER
        </a>
        <a
          href="#works"
          className="mobile-nav-link"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          WORKS
        </a>
        <a
          href="#about"
          className="mobile-nav-link"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          ABOUT
        </a>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img
            src="/top-image.jpg"
            alt="Hero background"
            className="hero-bg-img"
          />
          <div className="hero-bg-gradient"></div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="contact" className="section-main">
        <div className="container-main">
          <div className="section-label-vertical">NEWSLETTER</div>
          <div className="contact-layout">
            <div className="contact-content fade-in-up">
              <h3>Newsletter Registration</h3>
              <p className="contact-desc">
                Please be sure to sign up for our newsletter, so get notified of
                upcoming announcements!
              </p>
            </div>

            <form className="form-container fade-in-up" onSubmit={handleSubmit}>
              <div className="form-field">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.name ? "error" : ""}`}
                  placeholder=" "
                />
                <label className="form-label">Name</label>
                {formErrors.name && (
                  <span className="error-text">{formErrors.name}</span>
                )}
              </div>

              <div className="form-field">
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.age ? "error" : ""}`}
                  placeholder=" "
                  min="1"
                  max="150"
                />
                <label className="form-label">Age</label>
                {formErrors.age && (
                  <span className="error-text">{formErrors.age}</span>
                )}
              </div>

              <div className="form-field">
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className={`form-select ${formErrors.gender ? "error" : ""}`}
                >
                  <option value="">Please select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                <label className="form-label form-label-active">Gender</label>
                {formErrors.gender && (
                  <span className="error-text">{formErrors.gender}</span>
                )}
              </div>

              <div className="form-field">
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.nationality ? "error" : ""}`}
                  placeholder=" "
                />
                <label className="form-label">Nationality</label>
                {formErrors.nationality && (
                  <span className="error-text">{formErrors.nationality}</span>
                )}
              </div>

              <div className="form-field">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.email ? "error" : ""}`}
                  placeholder=" "
                />
                <label className="form-label">Email</label>
                {formErrors.email && (
                  <span className="error-text">{formErrors.email}</span>
                )}
              </div>

              <div className="form-field">
                <textarea
                  name="opinion"
                  value={formData.opinion}
                  onChange={handleInputChange}
                  className={`form-textarea ${formErrors.opinion ? "error" : ""}`}
                  placeholder=" "
                  maxLength={1000}
                ></textarea>
                <label className="form-label">Message</label>
                <span className="char-counter">
                  {formData.opinion.length}/1000
                </span>
                {formErrors.opinion && (
                  <span className="error-text">{formErrors.opinion}</span>
                )}
              </div>

              <button type="submit" className="submit-btn">
                SEND
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Works Section */}
      <section id="works" className="section-main">
        <div className="container-main">
          <div className="section-label-vertical">WORKS</div>
          <div className="works-list">
            <div className="work-card fade-in-up">
              <div className="work-visual">
                <img
                  src="/work_01.jpg"
                  alt="Hokusai Art Works Tour"
                  className="work-img"
                />
              </div>
              <div className="work-details">
                <p className="work-tag">ART TOUR / 2024-2025</p>
                <h3 className="work-name">HOKUSAI WORKS WORLD TOUR</h3>
                <p className="work-desc">
                  Katsushika Hokusai Art Works Tour of the World starts up in
                  America soon!
                  <br />
                  <strong>＜Upcoming Events＞</strong> Arizona (Frank Lloyd
                  Wright Museum etc), Los Angeles (Hollywood), Hawaii
                  (Honolulu), Singapore, Saudi Arabia (Abu Dhabi), Vienna
                  (Austria), France (Paris), Italy (Milano) and...Japan.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-main section-about">
        <div className="container-main">
          <div className="section-label-vertical">ABOUT</div>
          <div className="about-grid">
            <div className="about-text fade-in-up">
              <h2 className="section-heading">
                KATSUSHIKA
                <br />
                HOKUSAI
              </h2>
              <p className="about-desc">
                Katsushika Hokusai who is a great artist, an educator, an
                architect, and a multifaceted influencer. The global world is
                now waiting for Hokusai beyond 170 years!
              </p>
            </div>
            <div className="about-visual fade-in-up">
              <img src="/about_01.jpg" alt="About" className="about-img" />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-main">
        <div className="container-main">
          <div className="footer-end">
            <p>© 2025 HOKUSAI WORKS WORLD TOUR. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
